import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { Grid, Wrapper } from '@screentone/core';
import { isEqual } from 'lodash';

import { Article } from 'components/datamodel/content/Article/Article';
import PageDraftTrash from 'components/page-draft-trash/PageDraftTrash';
import type { SearchContentFilters } from 'components/search-content/components/search-content-filters/SearchContentFiltersContainer';
import { SearchContent, SearchParams, SearchProps } from 'components/search-content/SearchContent';
import { MenuActionsKeys } from 'contexts/context-menu-actions/ContextMenuActionsContext';
import { useContextMenuActions } from 'contexts/context-menu-actions/useContextMenuActions';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { DRAGGABLE_PREFIXES } from 'contexts/drag-and-drop/dragUtils';
import { useDragAndDrop } from 'contexts/drag-and-drop/useDragAndDrop';
import { ArticleItem, MultiListContainer, PublicationSettingSearchableContentType } from 'data/generated/graphql';
import { useElementDimensions } from 'hooks/dom/useElementDimensions';
import { usePublicationSettings } from 'hooks/publication-settings';
import { AllessehContent, AllessehQuerySortKeyTypes } from 'hooks/useAllessehContentQuery';
import { DEFAULT_CONTENT_SEARCH_FILTERS } from 'utils/constants';
import { Sidebar } from './components/sidebar/Sidebar';
import { Topbar } from './components/Topbar/Topbar';
import { SelectedCollectionsProvider, useSelectedCollections } from './contexts/SelectedCollectionsContext';
import styles from './MultiCollectionEdit.module.scss';

import { isArticleItem } from 'contexts/datamodel/DataModelContext.utils';

const MultiCollectionEditInner = () => {
  const { root, renderEntity, fromAllessehContent } = useDataModelContext<MultiListContainer>();
  const { renderActions } = useContextMenuActions();
  const { generateDraggableId } = useDragAndDrop();
  const { elementRef, dimensions } = useElementDimensions();
  const { data: publicationSettingsResp } = usePublicationSettings();
  const { activeTabId } = useSelectedCollections();

  const activeCollectionArticleIds = useMemo(() => {
    const activeCollection = root.collection.find((c) => c.uuid === activeTabId);
    return (
      activeCollection?.collection
        .filter(isArticleItem)
        .map((a) => a.attributes.id)
        .sort() ?? []
    );
  }, [activeTabId, root.collection]);

  const defaultFilterState = useMemo<SearchContentFilters>(
    () => ({
      ...DEFAULT_CONTENT_SEARCH_FILTERS,
      contentTypes: publicationSettingsResp?.publicationSetting.defaultContentTypes ?? [
        PublicationSettingSearchableContentType.Article
      ],
      excludedContentIds: activeCollectionArticleIds
    }),
    [activeCollectionArticleIds, publicationSettingsResp?.publicationSetting.defaultContentTypes]
  );
  const [searchParams, setSearchParams] = useState<SearchParams>({
    search: '',
    filters: defaultFilterState,
    sort: AllessehQuerySortKeyTypes.LiveDate
  });

  useLayoutEffect(() => {
    setSearchParams((prev) => {
      if (isEqual(prev.filters.excludedContentIds, activeCollectionArticleIds)) return prev;
      return { ...prev, filters: { ...prev.filters, excludedContentIds: activeCollectionArticleIds } };
    });
  }, [activeCollectionArticleIds]);

  const renderContentCardFn = useCallback(
    (content: AllessehContent, index: number) => (
      <Draggable draggableId={generateDraggableId(DRAGGABLE_PREFIXES.COLLECTION_ITEM, { content })} index={index}>
        {(draggableProvided) => {
          const articleItem = fromAllessehContent(content) as ArticleItem;
          return (
            <div
              ref={draggableProvided.innerRef}
              {...draggableProvided.draggableProps}
              {...draggableProvided.dragHandleProps}
            >
              <Article
                data={articleItem}
                renderActions={() =>
                  renderActions(MenuActionsKeys.ArticleSearch, {
                    entity: articleItem,
                    extraProperties: {
                      allessehContent: content
                    }
                  })
                }
              />
            </div>
          );
        }}
      </Draggable>
    ),
    [fromAllessehContent, generateDraggableId, renderActions]
  );

  const searchProps = useMemo<SearchProps>(
    () => ({
      defaultFilterState,
      searchParams,
      onSearchParamsChange: setSearchParams,
      renderContentCard: renderContentCardFn,
      resultListClassName: styles.searchContentList
    }),
    [defaultFilterState, renderContentCardFn, searchParams]
  );

  const draftSection = renderEntity(root, { hierarchyId: '' });

  return (
    <Grid>
      <Grid.Row xsCols={12} smCols={12}>
        <Grid.Col xs={4} sm={3} style={{ paddingLeft: 0 }}>
          <Sidebar />
        </Grid.Col>
        <Grid.Col xs={8} sm={9} className={styles.contentCol}>
          <Topbar />
          <Grid>
            <Grid.Row xsCols={12} mdCols={12}>
              <Grid.Col xs={6} md={7} className={styles.draftCol}>
                <Wrapper className={styles.draftSection}>{draftSection}</Wrapper>
              </Grid.Col>
              <Grid.Col xs={6} md={5} ref={elementRef} className={styles.searchCol}>
                <SearchContent
                  searchProps={searchProps}
                  beforeContent={
                    <PageDraftTrash
                      messageLabel="collection"
                      overrideStyle={{
                        top: 'calc(var(--app-page-header-height))',
                        left: 'unset',
                        right: 'var(--st-spacer-md)',
                        width: `${dimensions.scrollWidth}px`
                      }}
                    />
                  }
                />
              </Grid.Col>
            </Grid.Row>
          </Grid>
        </Grid.Col>
      </Grid.Row>
    </Grid>
  );
};

export const MultiCollectionEdit = () => (
  <SelectedCollectionsProvider>
    <MultiCollectionEditInner />
  </SelectedCollectionsProvider>
);
